import { z } from 'zod';

// Basic schema to validate the expected resource_list payload
const resourceSchema = z.object({
  capacity: z.number().optional(),
  id: z.number().optional(),
  email: z.string().optional(),
  isAvailable: z.boolean().optional(),
  locationId: z.number().optional(),
  locationPath: z.string().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
  categories: z.array(z.string()).optional(),
});

const resourceListSchema = z.object({
  type: z.literal('resource_list'),
  data: z.object({
    reply: z.string().optional(),
    resources: z.array(resourceSchema).default([]),
    infoPagination: z.string().optional(),
    hasNext: z.boolean().optional(),
    hasPrevious: z.boolean().optional(),
    currentPage: z.number().optional(),
    totalPage: z.number().optional(),
    pageSize: z.number().optional(),
    filters: z.any().optional(),
    sessionId: z.any().optional(),
  }).passthrough(),
}).passthrough();

export function parseLooseJson(input: string): any | null {
  if (!input || typeof input !== 'string') return null;
  let text = input.trim();

  // Extract inside code fences if present
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fenced && fenced[1]) {
    text = fenced[1].trim();
  }

  // Regex-strip EVERYTHING before the first JSON opening brace or bracket
  const firstBraceMatch = text.match(/[\[{]/);
  if (firstBraceMatch && typeof firstBraceMatch.index === 'number' && firstBraceMatch.index > 0) {
    text = text.slice(firstBraceMatch.index).trimStart();
  }

  // Fast path
  try {
    const parsed = JSON.parse(text);
    // Validate and normalize known payloads
    const safe = resourceListSchema.safeParse(parsed);
    return safe.success ? safe.data : parsed;
  } catch {}

  // Find first JSON object/array substring with brace matching
  const firstCurly = text.indexOf('{');
  const firstSquare = text.indexOf('[');
  let start = -1;
  if (firstCurly !== -1 && (firstSquare === -1 || firstCurly < firstSquare)) start = firstCurly;
  else if (firstSquare !== -1) start = firstSquare;
  if (start === -1) return null;

  let inString = false;
  let escape = false;
  const stack: string[] = [];
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escape) {
        escape = false;
      } else if (ch === '\\') {
        escape = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{' || ch === '[') stack.push(ch);
    else if (ch === '}' || ch === ']') {
      if (!stack.length) break;
      const open = stack.pop();
      if ((open === '{' && ch !== '}') || (open === '[' && ch !== ']')) return null;
      if (!stack.length) {
        const candidate = text.slice(start, i + 1);
        try {
          const parsed = JSON.parse(candidate);
          const safe = resourceListSchema.safeParse(parsed);
          return safe.success ? safe.data : parsed;
        } catch {
          // continue scanning in rare malformed cases
        }
      }
    }
  }
  return null;
}

/**
 * Lightweight self-test for parseLooseJson. Returns an array of test results
 * that you can log in dev tools. No external test framework required.
 */
export function testParseLooseJson() {
  const cases: { name: string; input: string; expect: any }[] = [
    {
      name: "Pure object JSON",
      input: '{"a":1}',
      expect: { a: 1 },
    },
    {
      name: "Text around object JSON",
      input: 'prefix {"a":1} suffix',
      expect: { a: 1 },
    },
    {
      name: "JSON code fence",
      input: '```json\n{"a":1}\n```',
      expect: { a: 1 },
    },
    {
      name: "Array JSON with trailing text",
      input: 'Before [1,2,3] After',
      expect: [1, 2, 3],
    },
    {
      name: "Embedded fenced complex JSON",
      input: 'Some text before ```json\n{"x":[1,2,3],"y":{"z":true}}\n``` and after',
      expect: { x: [1, 2, 3], y: { z: true } },
    },
    {
      name: "Not JSON",
      input: 'this is not json',
      expect: null,
    },
  ];

  return cases.map((c) => {
    let out: any = null;
    try {
      out = parseLooseJson(c.input);
    } catch (e) {
      out = `thrown:${(e as Error)?.message ?? "err"}`;
    }
    const passed = JSON.stringify(out) === JSON.stringify(c.expect);
    return { name: c.name, passed, input: c.input, output: out };
  });
}


